'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

const VISITOR_STORAGE_KEY = 'hestia_visitor_id';
const SCROLL_MILESTONES = [25, 50, 75, 90];

function getVisitorId() {
  if (typeof window === 'undefined') {
    return 'server';
  }

  const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const created = `visitor_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
  window.localStorage.setItem(VISITOR_STORAGE_KEY, created);
  return created;
}

function shouldTrackPath(pathname: string) {
  return (
    !pathname.includes('/jarvis') &&
    !pathname.includes('/admin') &&
    !pathname.includes('/super-admin')
  );
}

async function sendEvent(type: string, payload: Record<string, unknown>) {
  try {
    await fetch('/api/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type,
        ...payload,
      }),
      keepalive: true,
    });
  } catch (error) {
    console.warn('[AnalyticsTracker] event failed', type, error);
  }
}

export function AnalyticsTracker() {
  const pathname = usePathname();
  const sentViewRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || !shouldTrackPath(pathname)) {
      return;
    }

    const visitorId = getVisitorId();
    const pageKey = `${pathname}:${document.title}`;

    if (sentViewRef.current !== pageKey) {
      sentViewRef.current = pageKey;
      void sendEvent('page_view', {
        visitorId,
        path: pathname,
        title: document.title,
        referrer: document.referrer || null,
      });
    }

    const reached = new Set<number>();

    const handleScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) {
        return;
      }

      const progress = Math.round((window.scrollY / maxScroll) * 100);

      SCROLL_MILESTONES.forEach((milestone) => {
        if (progress >= milestone && !reached.has(milestone)) {
          reached.add(milestone);
          void sendEvent('scroll_depth', {
            visitorId,
            path: pathname,
            depth: milestone,
          });
        }
      });
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest('a') as HTMLAnchorElement | null;
      const button = target?.closest('button') as HTMLButtonElement | null;

      const href = link?.getAttribute('href') ?? '';
      const text = (link?.textContent || button?.textContent || '').trim().toLowerCase();

      if (href.startsWith('tel:')) {
        void sendEvent('click_call', {
          visitorId,
          path: pathname,
          href,
          label: text || 'call',
        });
        return;
      }

      if (href.includes('wa.me') || href.includes('whatsapp')) {
        void sendEvent('click_whatsapp', {
          visitorId,
          path: pathname,
          href,
          label: text || 'whatsapp',
        });
        return;
      }

      if (
        href.includes('/rendez-vous') ||
        text.includes('réserver') ||
        text.includes('book a call')
      ) {
        void sendEvent('click_booking', {
          visitorId,
          path: pathname,
          href: href || null,
          label: text || 'booking',
        });
        return;
      }

      if (
        href.includes('/contact') ||
        href.includes('/rendez-vous') ||
        text.includes('contact') ||
        text.includes('devis') ||
        text.includes('appel')
      ) {
        void sendEvent('click_contact', {
          visitorId,
          path: pathname,
          href: href || null,
          label: text || 'contact',
        });
      }
    };

    const startedForms = new WeakSet<HTMLFormElement>();

    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      const form = target?.closest('form') as HTMLFormElement | null;
      if (!form || startedForms.has(form)) {
        return;
      }

      startedForms.add(form);
      void sendEvent('form_start', {
        visitorId,
        path: pathname,
        formId: form.id || null,
        formType: form.getAttribute('data-form-type') || null,
      });
    };

    const handleSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement | null;
      if (!form) {
        return;
      }

      void sendEvent('form_submit', {
        visitorId,
        path: pathname,
        formId: form.id || null,
        formType: form.getAttribute('data-form-type') || null,
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('submit', handleSubmit);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('submit', handleSubmit);
    };
  }, [pathname]);

  return null;
}
